import { useState, useEffect, useMemo } from "react";
import { GraduationCap, Download, Printer, FileSpreadsheet, Search, Loader2, School, Calendar, Trophy } from "lucide-react";
import { apiService } from "@/services/api";
import { useAuthStore } from "@/store/authStore";
import { toast } from "sonner";
import { useLang } from "@/hooks/useLang";

const t = {
  fr: {
    title: "Élèves Admis", subtitle: "Liste des élèves admis par le conseil de classe",
    year: "Année scolaire", class: "Classe", allClasses: "Toutes les classes",
    total: "Total", avg: "Moy. générale", th: "TH", enc: "Enc.", fel: "Félic.",
    nextClass: "Classe suivante", search: "Rechercher...", loading: "Chargement...",
    noData: "Aucun élève admis trouvé", print: "Imprimer", exportPDF: "PDF", exportExcel: "Excel",
    matricule: "Matricule", name: "Nom et Prénom", gender: "Sx", average: "Moyenne", rank: "Rang",
    decision: "Décision", distinctions: "Distinctions",
  },
  en: {
    title: "Admitted Students", subtitle: "List of students admitted by class council",
    year: "Academic Year", class: "Class", allClasses: "All classes",
    total: "Total", avg: "Avg", th: "HR", enc: "Enc.", fel: "Congr.",
    nextClass: "Next class", search: "Search...", loading: "Loading...",
    noData: "No admitted students found", print: "Print", exportPDF: "PDF", exportExcel: "Excel",
    matricule: "ID", name: "Full Name", gender: "Gender", average: "Average", rank: "Rank",
    decision: "Decision", distinctions: "Distinctions",
  }
};

export default function CouncilAdmittedPage() {
  const { lang } = useLang();
  const txt = t[lang];
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [academicYears, setAcademicYears] = useState<any[]>([]);
  const [classes, setClasses] = useState<any[]>([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [students, setStudents] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  const schoolId = user?.school_id || 1;

  useEffect(() => {
    apiService.get(`/schools/${schoolId}/academic-years`).then((res: any) => {
      const list = Array.isArray(res) ? res : res?.data || res?.items || [];
      setAcademicYears(list);
      if (list[0]) setSelectedYear(String(list[0].id));
    });
    apiService.get("/classes", { params: { school_id: schoolId, per_page: 1000 } }).then((res: any) => {
      const list = Array.isArray(res) ? res : res?.data || res?.items || [];
      setClasses(list);
    });
  }, [schoolId]);

  useEffect(() => {
    if (!selectedYear) return;
    setLoading(true);
    const params: any = { academic_year_id: selectedYear };
    if (selectedClass) params.class_id = selectedClass;
    apiService.get("/class-council/reports/admis", { params })
      .then((res: any) => {
        const data = res?.data || res;
        setStudents(data?.items || []);
      })
      .catch(() => toast.error(txt.loading))
      .finally(() => setLoading(false));
  }, [selectedYear, selectedClass]);

  const filtered = useMemo(() => {
    if (!searchQuery) return students;
    const q = searchQuery.toLowerCase();
    return students.filter((s: any) =>
      (s.full_name || "").toLowerCase().includes(q) ||
      (s.matricule || "").toLowerCase().includes(q)
    );
  }, [students, searchQuery]);

  const stats = useMemo(() => ({
    total: filtered.length,
    avg: filtered.length > 0 ? (filtered.reduce((sum: number, s: any) => sum + (parseFloat(s.general_average) || 0), 0) / filtered.length).toFixed(2) : "0.00",
    th: filtered.filter((s: any) => s.tableau_honneur).length,
    enc: filtered.filter((s: any) => s.encouragements).length,
    fel: filtered.filter((s: any) => s.felicitations).length,
  }), [filtered]);

  const exportExcel = () => {
    const headers = ["Matricule", "Nom", "Classe", "Moyenne", "Rang", "Distinctions", "Classe suivante"];
    const rows = filtered.map((s: any) => [
      s.matricule, `${s.first_name} ${s.last_name}`, s.class_name,
      s.general_average || "-", s.annual_rank || "-",
      [s.tableau_honneur ? "TH" : "", s.encouragements ? "Enc" : "", s.felicitations ? "Félic" : ""].filter(Boolean).join(", "),
      s.next_class_name || "-"
    ]);
    const csv = [headers.join(";"), ...rows.map((r: any) => r.join(";"))].join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `admis_${selectedYear}.csv`;
    link.click();
    toast.success("Excel téléchargé");
  };

  const printPage = () => window.print();

  return (
    <div className="min-h-screen bg-gray-50 p-6 print:p-0">
      <div className="mb-6 print:hidden">
        <h1 className="text-2xl font-bold text-emerald-700 flex items-center gap-2">
          <Trophy className="w-7 h-7" /> {txt.title}
        </h1>
        <p className="text-sm text-gray-500">{txt.subtitle}</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4 print:hidden flex flex-wrap items-center gap-3">
        <select value={selectedYear} onChange={e => setSelectedYear(e.target.value)} className="px-3 py-2 border rounded-lg text-sm">
          <option value="">{txt.year}</option>
          {academicYears.map(y => <option key={y.id} value={String(y.id)}>{y.name || y.label}</option>)}
        </select>
        <select value={selectedClass} onChange={e => setSelectedClass(e.target.value)} className="px-3 py-2 border rounded-lg text-sm">
          <option value="">{txt.allClasses}</option>
          {classes.map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
        </select>
        <div className="h-8 w-px bg-gray-200" />
        <div className="flex gap-2">
          <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-bold">{stats.total} {txt.total}</span>
          <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-bold">{txt.avg}: {stats.avg}</span>
          {stats.th > 0 && <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-bold">{stats.th} TH</span>}
          {stats.enc > 0 && <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-bold">{stats.enc} Enc</span>}
        </div>
        <div className="flex-1" />
        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input type="text" placeholder={txt.search} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 pr-4 py-2 border rounded-lg text-sm w-40" />
        </div>
        <button onClick={printPage} className="p-2 hover:bg-gray-100 rounded-lg"><Printer className="w-4 h-4" /></button>
        <button onClick={exportExcel} className="p-2 hover:bg-gray-100 rounded-lg text-emerald-600"><FileSpreadsheet className="w-4 h-4" /></button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase">{txt.matricule}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase">{txt.name}</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase">{txt.class}</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase">{txt.average}</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase">{txt.rank}</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase">{txt.distinctions}</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase">{txt.nextClass}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr><td colSpan={7} className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} className="py-12 text-center text-gray-400">{txt.noData}</td></tr>
              ) : filtered.map((s: any, i: number) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-[#1e3a8a]">{s.matricule}</td>
                  <td className="px-4 py-3 font-medium">{s.first_name} {s.last_name}</td>
                  <td className="px-4 py-3 text-center">{s.class_name}</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-700">{s.general_average ? parseFloat(s.general_average).toFixed(2) : "-"}</td>
                  <td className="px-4 py-3 text-center">{s.annual_rank || "-"}</td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex gap-1 justify-center">
                      {s.tableau_honneur && <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px] font-bold">TH</span>}
                      {s.encouragements && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-bold">Enc</span>}
                      {s.felicitations && <span className="px-1.5 py-0.5 bg-cyan-100 text-cyan-700 rounded text-[10px] font-bold">Félic</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center text-emerald-700 font-medium">{s.next_class_name || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}